import { LeftOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Divider, Flex, Table } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { MenuProps } from 'antd/lib'
import { useNavigate } from 'react-router-dom'

import {
  useClassifierServiceDeleteInvestigationTestTypeEndpoint,
  useClassifierServiceGetListInvestigationTestTypeEndpoint,
  useClassifierServiceGetListInvestigationTestTypeEndpointKey
} from 'src/api/queries'
import { ApiError, InvestigationTestTypeItemDto } from 'src/api/requests'
import {
  DeleteConfirm,
  message,
  PageBreadcrumbs,
  TableActionButtons
} from 'src/components'
import { useTablePagination } from 'src/hooks'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

/**
 * Test types are added and edited inside their investigation type (InvestigationTypeModal);
 * this list only shows them and deletes them.
 */

export const TestTypesPage = () => {
  const { t, currentLang } = useLocales()

  const navigate = useNavigate()
  const pagination = useTablePagination()
  const queryClient = useQueryClient()

  const queryParams = {
    pageNumber: Number(pagination.current),
    pageSize: Number(pagination.pageSize)
  }

  const { data, isLoading } =
    useClassifierServiceGetListInvestigationTestTypeEndpoint(queryParams)

  const { mutate } = useClassifierServiceDeleteInvestigationTestTypeEndpoint({
    onError: (err: ApiError) => formErrorHandler(err.body.errors),
    onSuccess: () => {
      message.success(t('laboratory_classifiers.delete_success_test_type'))
      queryClient.invalidateQueries({
        queryKey: [useClassifierServiceGetListInvestigationTestTypeEndpointKey]
      })
    }
  })

  const onBackClick = () => navigate(-1)

  const getItems = (id: string): MenuProps['items'] => [
    {
      key: 'delete',
      danger: true,
      label: (
        <DeleteConfirm
          title={t('laboratory_classifiers.delete_test_type')}
          onConfirm={() =>
            mutate({
              id: String(id)
            })
          }
        />
      )
    }
  ]

  const columns: ColumnsType<InvestigationTestTypeItemDto> = [
    {
      key: 'name',
      title: t('medicine_register.products.name'),
      render: (_, { title }) => title?.[currentLang?.value]
    },
    {
      key: 'category',
      title: t('laboratory_classifiers.category'),
      render: (_, { type }) =>
        type
          ? t(`laboratory_classifiers.test_type_category_options.${type}`)
          : '—'
    },

    {
      key: 'comments',
      title: t('common.comments'),
      render: (_, { comments }) => comments || '-'
    },
    {
      key: 'order',
      title: t('common.order'),
      render: (_, { order }) => order ?? '-'
    },
    {
      key: 'actions',
      render: (_, { id }) => (
        <TableActionButtons justify="flex-end" items={getItems(String(id))} />
      )
    }
  ]

  return (
    <PageBreadcrumbs
      title={t('laboratory_classifiers.test_types')}
      items={[
        { title: t('menu.laboratory_classifiers') },
        { title: t('laboratory_classifiers.test_types') }
      ]}
    >
      <Flex vertical>
        <Flex justify="space-between">
          <Button icon={<LeftOutlined />} onClick={onBackClick}>
            {t('common.back')}
          </Button>
        </Flex>

        <Divider />

        <Table
          rowKey="id"
          bordered
          loading={isLoading}
          dataSource={data?.data}
          columns={columns}
          pagination={{ ...pagination, total: data?.totalCount }}
        />
      </Flex>
    </PageBreadcrumbs>
  )
}
